import React, { useState, useEffect } from 'react';
import { Bot, Cpu, Smartphone, Hand, AlertCircle, Calendar, X, ChevronDown, Clock, Users, Sun, Moon } from 'lucide-react';

const LaborForecastingTool = () => {
  const [pickingRates, setPickingRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTimelineView, setSelectedTimelineView] = useState(7);
  const [timelineDates, setTimelineDates] = useState([]);
  const [ordersData, setOrdersData] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [poDetails, setPODetails] = useState([]);
  const [poDetailsLoading, setPODetailsLoading] = useState(false);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({}); // Track which dropdowns are open
  const [updatingPickMethod, setUpdatingPickMethod] = useState({}); // Track which POs are being updated
  const [forecastData, setForecastData] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [hasPickMethodChanges, setHasPickMethodChanges] = useState(false); // Track if any pick methods have been changed
  const [recalculatingForecast, setRecalculatingForecast] = useState(false); // Track recalculation state

  // Get API URL from environment or default
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';

  // Generate timeline dates starting from today
  const generateTimelineDates = (days) => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  // Format date for display
  const formatDate = (date) => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return {
      dayOfWeek: dayNames[date.getDay()],
      dayNumber: date.getDate(),
      month: monthNames[date.getMonth()],
      fullDate: date.toISOString().split('T')[0] // YYYY-MM-DD format
    };
  };

  // Fetch picking rates data on component mount
  useEffect(() => {
    const fetchPickingRates = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get token
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication required');
        }

        // Make API request
        const response = await fetch(`${API_URL}/labor-forecasting/picking-rates`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch picking rates data');
        }

        const result = await response.json();
        if (result.success) {
          setPickingRates(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch picking rates data');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching picking rates:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPickingRates();
  }, [API_URL]);

  // Fetch PO details for a specific date
  const fetchPODetails = async (date) => {
    try {
      setPODetailsLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${API_URL}/labor-forecasting/po-details/${date}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch PO details');
      }

      const result = await response.json();
      if (result.success) {
        setPODetails(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch PO details');
      }
    } catch (err) {
      console.error('Error fetching PO details:', err);
      setPODetails([]);
    } finally {
      setPODetailsLoading(false);
    }
  };

  // Fetch daily pick forecast for a specific date
  const fetchDailyPickForecast = async (date) => {
    try {
      setForecastLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${API_URL}/labor-forecasting/daily-pick-forecast/${date}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch daily pick forecast');
      }

      const result = await response.json();
      if (result.success) {
        console.log('🔍 Forecast Data Debug - Raw API response:', result);
        console.log('🔍 Forecast Data Debug - Data type:', typeof result.data);
        console.log('🔍 Forecast Data Debug - Is Array:', Array.isArray(result.data));
        console.log('🔍 Forecast Data Debug - Data content:', result.data);
        setForecastData(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch daily pick forecast');
      }
    } catch (err) {
      console.error('Error fetching daily pick forecast:', err);
      setForecastData(null);
    } finally {
      setForecastLoading(false);
    }
  };

  // Fetch orders data for the timeline
  const fetchOrdersData = async (startDate, endDate) => {
    try {
      setOrdersLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${API_URL}/labor-forecasting/orders-by-date?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch orders data');
      }

      const result = await response.json();
      if (result.success) {
        setOrdersData(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch orders data');
      }
    } catch (err) {
      console.error('Error fetching orders data:', err);
      setOrdersData([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Update timeline dates when selectedTimelineView changes
  useEffect(() => {
    const dates = generateTimelineDates(selectedTimelineView);
    setTimelineDates(dates);

    // Fetch orders data for the new date range
    if (dates.length > 0) {
      const startDate = dates[0].toISOString().split('T')[0];
      const endDate = dates[dates.length - 1].toISOString().split('T')[0];
      fetchOrdersData(startDate, endDate);
    }
  }, [selectedTimelineView, API_URL]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside any dropdown
      if (!event.target.closest('.dropdown-container')) {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Format number for display
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Math.round(num).toLocaleString();
  };

  // Get all metrics for a specific date
  const getMetricsForDate = (dateString) => {
    const orderEntry = ordersData.find(entry => {
      const entryDate = new Date(entry.due_date).toISOString().split('T')[0];
      return entryDate === dateString;
    });
    return orderEntry ? {
      orders: parseInt(orderEntry.orders) || 0,
      totalLines: parseInt(orderEntry.total_lines) || 0,
      totalEaches: parseInt(orderEntry.total_eaches) || 0
    } : {
      orders: 0,
      totalLines: 0,
      totalEaches: 0
    };
  };

  // Handle day tile click
  const handleDayTileClick = async (dateString) => {
    setSelectedDate(dateString);
    setShowDetailsPanel(true);
    // Reset change tracking when selecting a different date
    setHasPickMethodChanges(false);
    setRecalculatingForecast(false);
    await Promise.all([
      fetchPODetails(dateString),
      fetchDailyPickForecast(dateString)
    ]);
  };

  // Close details panel
  const closeDetailsPanel = () => {
    setShowDetailsPanel(false);
    setSelectedDate(null);
    setPODetails([]);
    setForecastData(null);
    setOpenDropdowns({});
    setUpdatingPickMethod({});
    setHasPickMethodChanges(false);
    setRecalculatingForecast(false);
  };

  // Update pick method for a specific PO
  const updatePickMethod = async (date, po, newPickMethod) => {
    const updateKey = `${date}-${po}`;

    try {
      setUpdatingPickMethod(prev => ({ ...prev, [updateKey]: true }));

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${API_URL}/labor-forecasting/po-pick-method/${date}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            po: po,
            pickMethod: newPickMethod
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update pick method');
      }

      const result = await response.json();
      if (result.success) {
        // Update the local state to reflect the change
        setPODetails(prevDetails =>
          prevDetails.map(detail =>
            detail.po === po ? { ...detail, pick_method: newPickMethod } : detail
          )
        );

        // Mark that pick methods have been changed
        setHasPickMethodChanges(true);

        // Close the dropdown
        setOpenDropdowns(prev => ({ ...prev, [updateKey]: false }));
      } else {
        throw new Error(result.message || 'Failed to update pick method');
      }
    } catch (err) {
      console.error('Error updating pick method:', err);
      // You might want to show a toast notification here
    } finally {
      setUpdatingPickMethod(prev => ({ ...prev, [updateKey]: false }));
    }
  };

  // Toggle dropdown open/close
  const toggleDropdown = (date, po) => {
    const updateKey = `${date}-${po}`;
    setOpenDropdowns(prev => ({ ...prev, [updateKey]: !prev[updateKey] }));
  };

  // Recalculate forecast based on updated pick methods
  const recalculateForecast = async () => {
    try {
      setRecalculatingForecast(true);

      // Re-fetch the forecast data which will include updated pick methods
      await fetchDailyPickForecast(selectedDate);

      // Reset the change tracking flag
      setHasPickMethodChanges(false);
    } catch (err) {
      console.error('Error recalculating forecast:', err);
      // Keep the change flag if recalculation fails
    } finally {
      setRecalculatingForecast(false);
    }
  };

  // Get responsive text sizes based on timeline view
  const getMetricsSizes = () => {
    if (selectedTimelineView === 7) {
      // 7-day view: larger tiles, larger text
      return {
        ordersSize: 'text-lg',
        metricsSize: 'text-sm',
        labelSize: 'text-xs'
      };
    } else {
      // 14-day and 30-day views: same tile width, same smaller text
      return {
        ordersSize: 'text-base',
        metricsSize: 'text-xs',
        labelSize: 'text-xs'
      };
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6">
      {/* Error display */}
      {error && (
        <div className="mb-6 p-4 bg-red-700/40 border border-red-600 rounded flex items-start">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-slate-400">Loading picking rates...</span>
        </div>
      )}

      {/* Picking Rates Metrics */}
      {!loading && !error && pickingRates && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Average Bot UPH */}
          <div className="bg-slate-700/50 rounded-lg py-2 px-3">
            <div className="flex items-center mb-2">
              <Bot className="w-5 h-5 text-blue-400 mr-2" />
              <h3 className="text-base font-medium text-slate-200">Average Bot UPH</h3>
            </div>
            <p className="text-xl font-bold text-blue-400">
              {formatNumber(pickingRates.averageBotUph)} <span className="text-slate-400 text-sm font-normal">Units per hour</span>
            </p>
          </div>

          {/* Average Bot LPH */}
          <div className="bg-slate-700/50 rounded-lg py-2 px-3">
            <div className="flex items-center mb-2">
              <Cpu className="w-5 h-5 text-green-400 mr-2" />
              <h3 className="text-base font-medium text-slate-200">Average Bot LPH</h3>
            </div>
            <p className="text-xl font-bold text-green-400">
              {formatNumber(pickingRates.averageBotLph)} <span className="text-slate-400 text-sm font-normal">Lines per hour</span>
            </p>
          </div>

          {/* Average Handheld UPH */}
          <div className="bg-slate-700/50 rounded-lg py-2 px-3">
            <div className="flex items-center mb-2">
              <Smartphone className="w-5 h-5 text-orange-400 mr-2" />
              <h3 className="text-base font-medium text-slate-200">Average Handheld UPH</h3>
            </div>
            <p className="text-xl font-bold text-orange-400">
              {formatNumber(pickingRates.averageHandheldUph)} <span className="text-slate-400 text-sm font-normal">Units per hour</span>
            </p>
          </div>

          {/* Average Handheld LPH */}
          <div className="bg-slate-700/50 rounded-lg py-2 px-3">
            <div className="flex items-center mb-2">
              <Hand className="w-5 h-5 text-purple-400 mr-2" />
              <h3 className="text-base font-medium text-slate-200">Average Handheld LPH</h3>
            </div>
            <p className="text-xl font-bold text-purple-400">
              {formatNumber(pickingRates.averageHandheldLph)} <span className="text-slate-400 text-sm font-normal">Lines per hour</span>
            </p>
          </div>
        </div>
      )}

      {/* Timeline Section */}
      {!loading && !error && (
        <div className="mt-8">
          {/* Timeline View Selector */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-slate-200 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Timeline View
            </h2>
            <div className="flex bg-slate-700 rounded-lg p-1">
              {[7, 14, 30].map((days) => (
                <button
                  key={days}
                  onClick={() => setSelectedTimelineView(days)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedTimelineView === days
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-300 hover:text-white hover:bg-slate-600'
                  }`}
                >
                  {days} Days
                </button>
              ))}
            </div>
          </div>

          {/* Timeline Days */}
          <div className={`${selectedTimelineView === 30 ? "overflow-x-auto" : "overflow-hidden"} px-1 py-2`}>
            <div className="flex gap-3 pb-2">
              {timelineDates.map((date, index) => {
                const formattedDate = formatDate(date);
                const isToday = index === 0;
                const showMonth = index === 0 || formattedDate.dayNumber === 1;

                // Dynamic tile sizing based on timeline view
                const getTileClasses = () => {
                  const baseClasses = "bg-slate-700/50 rounded-lg p-3 cursor-pointer transition-all duration-200 hover:bg-slate-600/50 hover:scale-105 hover:z-10 relative";
                  const heightClasses = "h-40"; // Another 25% taller (160px)
                  const isSelected = selectedDate === formattedDate.fullDate;

                  let statusClasses = '';
                  if (isSelected) {
                    statusClasses = 'ring-2 ring-green-500 bg-slate-600/70';
                  } else if (isToday) {
                    statusClasses = 'ring-2 ring-blue-500 bg-slate-600/50';
                  }

                  if (selectedTimelineView === 7) {
                    // 7-day view: tiles expand to fill full width
                    return `${baseClasses} ${heightClasses} flex-1 ${statusClasses}`;
                  } else if (selectedTimelineView === 14) {
                    // 14-day view: calculated width to fit all tiles
                    return `${baseClasses} ${heightClasses} ${statusClasses}`;
                  } else {
                    // 30-day view: same size as 14-day but with flex-shrink-0 for scrolling
                    return `${baseClasses} ${heightClasses} flex-shrink-0 ${statusClasses}`;
                  }
                };

                // Calculate style for 14-day and 30-day views
                const getTileStyle = () => {
                  if (selectedTimelineView === 14 || selectedTimelineView === 30) {
                    // Calculate width to fit exactly the number of tiles with gaps
                    // Formula: (100% - gap_width * (tiles - 1)) / tiles
                    const tilesForWidth = selectedTimelineView === 14 ? 14 : 14; // 30-day uses same width as 14-day
                    return {
                      width: `calc((100% - ${(tilesForWidth - 1) * 12}px) / ${tilesForWidth})`
                    };
                  }
                  return {};
                };

                const metrics = getMetricsForDate(formattedDate.fullDate);
                const sizes = getMetricsSizes();
                const isSelected = selectedDate === formattedDate.fullDate;

                return (
                  <div
                    key={formattedDate.fullDate}
                    className={getTileClasses()}
                    style={getTileStyle()}
                    onClick={() => handleDayTileClick(formattedDate.fullDate)}
                  >
                    {/* Order count at top center */}
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 text-center">
                      {ordersLoading ? (
                        <div className="text-xs text-slate-400">...</div>
                      ) : (
                        <div>
                          <div className={`${sizes.ordersSize} font-bold leading-none ${
                            isSelected ? 'text-green-400' : isToday ? 'text-blue-400' : 'text-slate-200'
                          }`}>
                            {formatNumber(metrics.orders)}
                          </div>
                          <div className={`${sizes.labelSize} leading-none mt-0.5 ${
                            isSelected ? 'text-green-400' : isToday ? 'text-blue-400' : 'text-slate-400'
                          }`}>
                            Orders
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Lines and Eaches stacked on left side */}
                    {!ordersLoading && (
                      <div className="absolute bottom-4 left-1 text-left">
                        {/* Lines count */}
                        <div className="mb-1">
                          <div className={`${sizes.metricsSize} font-semibold leading-none ${
                            isSelected ? 'text-green-400' : isToday ? 'text-blue-400' : 'text-slate-200'
                          }`}>
                            {formatNumber(metrics.totalLines)}
                          </div>
                          <div className={`${sizes.labelSize} leading-none ${
                            isSelected ? 'text-green-400' : isToday ? 'text-blue-400' : 'text-slate-400'
                          }`}>
                            Lines
                          </div>
                        </div>

                        {/* Eaches count */}
                        <div>
                          <div className={`${sizes.metricsSize} font-semibold leading-none ${
                            isSelected ? 'text-green-400' : isToday ? 'text-blue-400' : 'text-slate-200'
                          }`}>
                            {formatNumber(metrics.totalEaches)}
                          </div>
                          <div className={`${sizes.labelSize} leading-none ${
                            isSelected ? 'text-green-400' : isToday ? 'text-blue-400' : 'text-slate-400'
                          }`}>
                            Eaches
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Date info in top-left corner */}
                    <div className="absolute top-2 left-2 text-left">
                      {/* Month indicator */}
                      {showMonth && (
                        <div className={`text-xs leading-none mb-0.5 ${
                          isSelected ? 'text-green-400' : isToday ? 'text-blue-400' : 'text-slate-400'
                        }`}>
                          {formattedDate.month}
                        </div>
                      )}

                      {/* Day of week */}
                      <div className={`text-xs font-medium leading-none mb-0.5 ${
                        isSelected ? 'text-green-400' : isToday ? 'text-blue-400' : 'text-slate-300'
                      }`}>
                        {formattedDate.dayOfWeek}
                      </div>

                      {/* Day number */}
                      <div className={`text-sm font-bold leading-none ${
                        isSelected ? 'text-green-400' : isToday ? 'text-blue-400' : 'text-slate-200'
                      }`}>
                        {formattedDate.dayNumber}
                      </div>
                    </div>

                    {/* Today indicator in bottom-right corner */}
                    {isToday && (
                      <div className="absolute bottom-2 right-2 text-xs text-blue-400">
                        Today
                      </div>
                    )}

                    {/* Center area available for future content */}
                    <div className="flex items-center justify-center h-full">
                      {/* Future content will go here */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* PO Details Panel */}
      {showDetailsPanel && (
        <div className="mt-8 bg-slate-800 border border-slate-700 rounded-lg shadow-sm transform transition-all duration-300 ease-out opacity-100 translate-y-0">
            {/* Panel Header with Totals */}
            <div className="flex items-center justify-between p-2 border-b border-slate-700">
              <div className="flex items-center space-x-4">
                <h3 className="text-lg font-semibold text-slate-200">
                  PO Details for {selectedDate && new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })}
                </h3>
                {!poDetailsLoading && poDetails.length > 0 && (
                  <div className="flex items-center space-x-3 text-xs">
                    <span className="text-orange-400 font-semibold">
                      {formatNumber(poDetails.reduce((sum, po) => sum + (parseFloat(po.total_orders) || 0), 0))} Orders
                    </span>
                    <span className="text-blue-400 font-semibold">
                      {formatNumber(poDetails.reduce((sum, po) => sum + (parseFloat(po.total_lines) || 0), 0))} Lines
                    </span>
                    <span className="text-green-400 font-semibold">
                      {formatNumber(poDetails.reduce((sum, po) => sum + (parseFloat(po.total_eaches) || 0), 0))} Eaches
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={closeDetailsPanel}
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Daily Pick Forecast Metrics */}
            {!forecastLoading && forecastData && Array.isArray(forecastData) && forecastData.length > 0 && (
              <div className="p-4 border-b border-slate-700">
                <h4 className="text-md font-semibold text-slate-200 mb-3 flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  Daily Pick Forecast
                </h4>

                {/* Show recalculation button if pick methods have been changed */}
                {hasPickMethodChanges ? (
                  <div className="flex justify-center">
                    <button
                      onClick={recalculateForecast}
                      disabled={recalculatingForecast}
                      className="bg-amber-600 hover:bg-amber-700 disabled:bg-amber-600/50 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:cursor-not-allowed text-sm text-center max-w-lg"
                    >
                      {recalculatingForecast ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Recalculating...
                        </div>
                      ) : (
                        'Changes have been made to PO Pick Methods, please continue with further changes and click this to recalculate when ready'
                      )}
                    </button>
                  </div>
                ) : (
                  /* Show normal forecast tiles */
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {forecastData.map((record, index) => {
                      if (!record || typeof record !== 'object') {
                        console.warn('Invalid forecast record:', record);
                        return null;
                      }

                      const isBots = record.pick_method?.toLowerCase() === 'bots';
                      const IconComponent = isBots ? Bot : Smartphone;

                      return (
                        <React.Fragment key={index}>
                          {/* Day Shift */}
                          <div className={isBots ? 'bg-blue-600/20 border border-blue-500/30 rounded-lg p-3' : 'bg-orange-600/20 border border-orange-500/30 rounded-lg p-3'}>
                            <div className="flex items-center mb-2">
                              <IconComponent className={isBots ? 'w-4 h-4 text-blue-400 mr-2' : 'w-4 h-4 text-orange-400 mr-2'} />
                              <Sun className="w-3 h-3 text-yellow-400 mr-1" />
                              <span className="text-xs font-medium text-slate-200">
                                {record.pick_method} Day
                              </span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400">Work Hours</span>
                                <span className={isBots ? 'text-sm font-bold text-blue-400' : 'text-sm font-bold text-orange-400'}>
                                  {parseFloat(record.day_hours_lines || 0).toFixed(2)}h
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400">Employees</span>
                                <span className={isBots ? 'text-sm font-bold text-blue-400' : 'text-sm font-bold text-orange-400'}>
                                  {parseInt(record.day_needed_employees_lines_8h || 0)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Night Shift */}
                          <div className={isBots ? 'bg-blue-600/20 border border-blue-500/30 rounded-lg p-3' : 'bg-orange-600/20 border border-orange-500/30 rounded-lg p-3'}>
                            <div className="flex items-center mb-2">
                              <IconComponent className={isBots ? 'w-4 h-4 text-blue-400 mr-2' : 'w-4 h-4 text-orange-400 mr-2'} />
                              <Moon className="w-3 h-3 text-slate-400 mr-1" />
                              <span className="text-xs font-medium text-slate-200">
                                {record.pick_method} Night
                              </span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400">Work Hours</span>
                                <span className={isBots ? 'text-sm font-bold text-blue-400' : 'text-sm font-bold text-orange-400'}>
                                  {parseFloat(record.night_hours_lines || 0).toFixed(2)}h
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400">Employees</span>
                                <span className={isBots ? 'text-sm font-bold text-blue-400' : 'text-sm font-bold text-orange-400'}>
                                  {parseInt(record.night_needed_employees_lines_8h || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Forecast Loading State */}
            {forecastLoading && (
              <div className="p-4 border-b border-slate-700">
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-slate-400 text-sm">Loading forecast...</span>
                </div>
              </div>
            )}

            {/* Forecast Debug Info */}
            {!forecastLoading && (!forecastData || !Array.isArray(forecastData) || forecastData.length === 0) && (
              <div className="p-4 border-b border-slate-700">
                <div className="text-center py-4">
                  <span className="text-slate-400 text-sm">
                    {!forecastData ? 'No forecast data available' :
                     !Array.isArray(forecastData) ? 'Invalid forecast data format' :
                     'No forecast records found for this date'}
                  </span>
                </div>
              </div>
            )}

            {/* Panel Content */}
            <div className="p-2">
              {poDetailsLoading ? (
                <div className="flex justify-center items-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-slate-400 text-sm">Loading...</span>
                </div>
              ) : poDetails.length > 0 ? (
                <div className="overflow-x-auto">
                  {/* Single Unified Table */}
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-600">
                        <th className="text-left py-1 px-2 text-slate-300 font-medium">Business Unit</th>
                        <th className="text-left py-1 px-2 text-slate-300 font-medium">PO Number</th>
                        <th className="text-right py-1 px-2 text-slate-300 font-medium">Orders</th>
                        <th className="text-right py-1 px-2 text-slate-300 font-medium">Lines</th>
                        <th className="text-right py-1 px-2 text-slate-300 font-medium">Eaches</th>
                        <th className="text-center py-1 px-2 text-slate-300 font-medium">Pick Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(
                        poDetails.reduce((groups, po) => {
                          const unit = po.business_unit || 'Unknown';
                          if (!groups[unit]) groups[unit] = [];
                          groups[unit].push(po);
                          return groups;
                        }, {})
                      ).map(([businessUnit, pos]) => {
                        const businessUnitRows = pos.map((po, index) => (
                          <tr key={`${businessUnit}-${index}`} className="border-b border-slate-700/30 hover:bg-slate-700/10">
                            <td className="py-1 px-2 text-slate-300 text-xs">
                              {index === 0 ? (businessUnit.length > 12 ? businessUnit.substring(0, 12) + '...' : businessUnit) : ''}
                            </td>
                            <td className="py-1 px-2 text-slate-200 font-mono text-xs" title={po.po || '(No PO)'}>
                              {po.po ? (po.po.length > 15 ? po.po.substring(0, 15) + '...' : po.po) : '(No PO)'}
                            </td>
                            <td className="py-1 px-2 text-slate-200 text-right">
                              {formatNumber(po.total_orders)}
                            </td>
                            <td className="py-1 px-2 text-slate-200 text-right">
                              {formatNumber(po.total_lines)}
                            </td>
                            <td className="py-1 px-2 text-slate-200 text-right">
                              {formatNumber(po.total_eaches)}
                            </td>
                            <td className="py-1 px-2 text-center relative">
                              {/* Pick Method Dropdown */}
                              <div className="relative inline-block dropdown-container">
                                <button
                                  onClick={() => toggleDropdown(selectedDate, po.po)}
                                  disabled={updatingPickMethod[`${selectedDate}-${po.po}`]}
                                  className="flex items-center justify-center space-x-1 px-2 py-1 bg-slate-600/50 rounded text-xs text-slate-200 hover:bg-slate-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px]"
                                >
                                  {updatingPickMethod[`${selectedDate}-${po.po}`] ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b border-slate-300"></div>
                                  ) : (
                                    <>
                                      <span>{po.pick_method || 'Not Set'}</span>
                                      <ChevronDown className="w-3 h-3" />
                                    </>
                                  )}
                                </button>

                                {/* Dropdown Menu */}
                                {openDropdowns[`${selectedDate}-${po.po}`] && (
                                  <div className="absolute z-50 top-full left-1/2 transform -translate-x-1/2 mt-1 bg-slate-700 border border-slate-600 rounded shadow-lg min-w-[80px]">
                                    <button
                                      onClick={() => updatePickMethod(selectedDate, po.po, 'Bots')}
                                      className="block w-full px-3 py-1 text-xs text-slate-200 hover:bg-slate-600 transition-colors text-center"
                                    >
                                      Bots
                                    </button>
                                    <button
                                      onClick={() => updatePickMethod(selectedDate, po.po, 'Handheld')}
                                      className="block w-full px-3 py-1 text-xs text-slate-200 hover:bg-slate-600 transition-colors text-center"
                                    >
                                      Handheld
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ));

                        // Add subtotal row for each business unit (if more than 1 PO)
                        if (pos.length > 1) {
                          businessUnitRows.push(
                            <tr key={`${businessUnit}-subtotal`} className="bg-slate-700/20 border-b border-slate-600">
                              <td className="py-1 px-2 text-slate-300 font-semibold text-xs">
                                {businessUnit.length > 12 ? businessUnit.substring(0, 12) + '...' : businessUnit} Total
                              </td>
                              <td className="py-1 px-2 text-slate-400 text-xs italic">
                                {pos.length} POs
                              </td>
                              <td className="py-1 px-2 text-slate-300 font-semibold text-right">
                                {formatNumber(pos.reduce((sum, po) => sum + (parseFloat(po.total_orders) || 0), 0))}
                              </td>
                              <td className="py-1 px-2 text-slate-300 font-semibold text-right">
                                {formatNumber(pos.reduce((sum, po) => sum + (parseFloat(po.total_lines) || 0), 0))}
                              </td>
                              <td className="py-1 px-2 text-slate-300 font-semibold text-right">
                                {formatNumber(pos.reduce((sum, po) => sum + (parseFloat(po.total_eaches) || 0), 0))}
                              </td>
                              <td className="py-1 px-2 text-center">
                                <span className="text-slate-400 text-xs">-</span>
                              </td>
                            </tr>
                          );
                        }

                        return businessUnitRows;
                      }).flat()}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-slate-400 text-sm">No PO data found for this date.</p>
                </div>
              )}
            </div>
        </div>
      )}

    </div>
  );
};

export default LaborForecastingTool;